<template>
  <VFormItem>
    <VDateInput
      ref="dateInput"
      v-model="date"
      :label="dateLabel"
      :layout="layout"
      :required="required"
      :disabled="disabled"
      :validate-method="validateDate"
      @input="onDateChange"
    />

    <VInputMask
      ref="timeInput"
      mask="##:##"
      masked
      :label="timeLabel"
      :layout="layout"
      :required="required"
      :disabled="disabled"
      :value="time"
      :validate-method="validateTime"
      @input="onTimeChange"
    />
  </VFormItem>
</template>

<script lang="ts">

import Utils      from "@/core/utils/Utils";
import DateSimple from "@/core/types/DateSimple";
import TimeStamp  from "@/core/types/TimeStamp";

import VDateInput from "@/lib/components/VDateInput/VDateInput.vue";
import VInputMask from "@/lib/components/VInputMask/VInputMask.vue";

type ValidateMethod<T> = (value: T) => string | null;

export default defineComponent({
  name: 'VDateTimeInput',

  components: {
    VDateInput,
    VInputMask,
  },

  props: {
    modelValue: {
      type:    TimeStamp as PropType<TimeStamp | null>,
      default: null,
    },
    dateLabel: {
      type:    String,
      default: 'Дата',
    },
    timeLabel: {
      type:    String,
      default: 'Время',
    },
    layout: {
      type:    String as PropType<'vertical' | 'horizontal'>,
      default: 'vertical',
    },
    required: {
      type:    Boolean,
      default: false,
    },
    disabled: {
      type:    Boolean,
      default: false,
    },
    /** Кастомный метод валидации, получает полный TimeStamp и возвращает текст ошибки или null */
    validateMethod: {
      type:    Function as PropType<ValidateMethod<TimeStamp>>,
      default: null,
    },
    /**
     * Минимально допустимый момент. Дни до него гасятся в календаре, а сам
     * момент проверяется с точностью до минут: граница часто приходится на
     * тот же день (начало и конец торгов), и посуточной проверки мало.
     */
    minDate: {
      type:    [Date, DateSimple, TimeStamp] as PropType<Date | DateSimple | TimeStamp | null>,
      default: null,
    },
    /** Максимально допустимый момент. Проверяется так же, как minDate. */
    maxDate: {
      type:    [Date, DateSimple, TimeStamp] as PropType<Date | DateSimple | TimeStamp | null>,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      date: null as TimeStamp | null,
      time: '',
    };
  },

  watch: {
    modelValue: {
      handler(value: TimeStamp | null) {
        if (value instanceof TimeStamp) {
          this.date = value;

          const hours   = Utils.string.leftPad(this.date.getHours());
          const minutes = Utils.string.leftPad(this.date.getMinutes());

          this.time = `${hours}:${minutes}`;
        } else {
          this.date = null;
          this.time = '';
        }
      },
      immediate: true,
    },

    /** При смене границ перепроверяем дату и время, иначе останется старая ошибка */
    minDate: 'onMinMaxDateChange',
    maxDate: 'onMinMaxDateChange',
  },

  methods: {
    parseTime(): { hours: number; minutes: number } | null {
      if (this.time.length !== 5) { return null; }

      const [hours, minutes] = this.time.split(':').map(Number);

      if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null;
      }

      return { hours, minutes };
    },

    buildDateTime(): TimeStamp | null {
      if (!this.date) { return null; }

      const parsedTime = this.parseTime();
      if (!parsedTime) { return null; }

      const result = new TimeStamp(this.date);
      result.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);

      return result;
    },

    /**
     * Валидация даты — гасит недоступные ДНИ.
     *
     * Границы здесь округляются до дня сознательно: день границы гасить
     * нельзя, внутри него могут быть допустимые часы. Момент внутри дня
     * проверяет validateTime.
     */
    validateDate(dateSimple: DateSimple | null): string | null {
      if (!dateSimple) return null;

      if (dateSimple.isPast() === true) {
        return 'Нельзя указать прошедшую дату';
      }

      if (this.minDate && dateSimple.isBefore(new DateSimple(this.minDate)) === true) {
        return 'Дата недоступна';
      }

      if (this.maxDate && dateSimple.isAfter(new DateSimple(this.maxDate)) === true) {
        return 'Дата недоступна';
      }

      return null;
    },

    /**
     * Валидация момента целиком. Живёт на контроле времени, потому что время
     * уточняет уже выбранный день: границы minDate/maxDate часто приходятся
     * на тот же день, и посуточной проверки даты для них мало.
     *
     * Дата берётся из состояния, а не из аргумента — контрол времени о ней
     * не знает. При смене даты onDateChange перезапускает эту проверку.
     */
    validateTime(time: string): string | null {
      if (time && time.length === 5 && this.parseTime() === null) {
        return 'Некорректное время';
      }

      const dateTime = this.buildDateTime();

      if (dateTime === null) return null;

      if (dateTime.isFuture() === false) {
        return 'Нельзя указать прошедшее время';
      }

      if (this.minDate && dateTime.isAfter(this.minDate) === false) {
        return `Не раньше ${this.minDate}`;
      }

      if (this.maxDate && dateTime.isBefore(this.maxDate) === false) {
        return `Не позже ${this.maxDate}`;
      }

      if (this.validateMethod) {
        return this.validateMethod(dateTime);
      }

      return null;
    },

    onDateChange(date: Date): void {
      this.date = new TimeStamp(date);

      /** День сменился — момент мог выйти за границы, а контрол времени сам
       *  об этом не узнает: его собственное значение не менялось. */
      (this.$refs.timeInput as InstanceType<typeof VInputMask>).validate();

      this.emitDateTime();
    },

    onMinMaxDateChange(): void {
      if (this.date === null) return;

      (this.$refs.dateInput as InstanceType<typeof VDateInput>).validate();
      (this.$refs.timeInput as InstanceType<typeof VInputMask>).validate();
    },

    onTimeChange(time: string): void {
      this.time = time;
      this.emitDateTime();
    },

    emitDateTime(): void {
      const dateTime = this.buildDateTime();

      if (dateTime === null) return;

      this.$emit('update:modelValue', dateTime);
    },
  },
});
</script>
